import {
  Box,
  IconButton,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Stack,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  Refresh,
  Fullscreen,
  FullscreenExit,
} from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import { SchedulerDateRange } from '../types/scheduler.types';
import { useSchedulerContext } from '../context/SchedulerContext';

/** Hour options for the working day start dropdown (0–22; 22 is max so auto-bump keeps end ≤ 23) */
const START_HOUR_OPTIONS = Array.from({ length: 23 }, (_, i) => i);

/** Hour options for the working day end dropdown (1–23) */
const END_HOUR_OPTIONS = Array.from({ length: 23 }, (_, i) => i + 1);

/** Format an hour value (0–23) as a human-readable label, e.g. "5 AM", "12 PM" */
export function formatHourLabel(hour: number): string {
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
}

interface SchedulerToolbarProps {
  dateRange: SchedulerDateRange;
  timeZone: string;
  timeZoneOptions?: { title: string; value: string | number }[];
  onTimeZoneChange: (tz: string) => void;
  onRefresh: () => void;
  onFullScreen: () => void;
  isFullScreen: boolean;
  showNonWorkingTime?: boolean;
  onShowNonWorkingTimeChange?: (show: boolean) => void;
  /** Custom working day start hour (0–23) */
  workingDayStartHour?: number;
  /** Custom working day end hour (1–23; must be > startHour) */
  workingDayEndHour?: number;
  onWorkingDayStartHourChange?: (hour: number) => void;
  onWorkingDayEndHourChange?: (hour: number) => void;
}

export function SchedulerToolbar({
  dateRange,
  timeZone,
  timeZoneOptions = [],
  onTimeZoneChange,
  onRefresh,
  onFullScreen,
  isFullScreen,
  showNonWorkingTime,
  onShowNonWorkingTimeChange,
  workingDayStartHour,
  workingDayEndHour,
  onWorkingDayStartHourChange,
  onWorkingDayEndHourChange,
}: SchedulerToolbarProps) {
  const { shiftNext, shiftPrevious, shiftToday } = useSchedulerContext();

  // dateRange.endDate is exclusive (midnight of the day after the last
  // visible day), matching the API's half-open convention. The label
  // should show the inclusive last day — e.g. "May 1 - 7" for a week
  // whose endDate is May 8 00:00 — so subtract a day before formatting.
  const formattedDateRange =
    dateRange.startDate && dateRange.endDate
      ? `${format(dateRange.startDate, 'MMM d')} - ${format(
          subDays(dateRange.endDate, 1),
          'MMM d, yyyy'
        )}`
      : '';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {/* Left: Non-working time filter + Navigation */}
      <Stack direction="row" spacing={1} alignItems="center">
        {onShowNonWorkingTimeChange && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showNonWorkingTime ?? true}
                onChange={(e) => onShowNonWorkingTimeChange(e.target.checked)}
              />
            }
            label="Non-working time"
            sx={{ mr: 2 }}
          />
        )}

        {onWorkingDayStartHourChange && onWorkingDayEndHourChange && (
          <>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel id="working-day-start-label">Start</InputLabel>
              <Select
                labelId="working-day-start-label"
                value={workingDayStartHour ?? 5}
                label="Start"
                disabled={showNonWorkingTime === true}
                onChange={(e) =>
                  onWorkingDayStartHourChange(Number(e.target.value))
                }
              >
                {START_HOUR_OPTIONS.map((h) => (
                  <MenuItem key={h} value={h}>
                    {formatHourLabel(h)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel id="working-day-end-label">End</InputLabel>
              <Select
                labelId="working-day-end-label"
                value={workingDayEndHour ?? 21}
                label="End"
                disabled={showNonWorkingTime === true}
                onChange={(e) =>
                  onWorkingDayEndHourChange(Number(e.target.value))
                }
              >
                {END_HOUR_OPTIONS.map((h) => (
                  <MenuItem key={h} value={h}>
                    {formatHourLabel(h)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        <Tooltip title="Previous week">
          <IconButton onClick={shiftPrevious} size="small">
            <ChevronLeft />
          </IconButton>
        </Tooltip>

        <Button
          variant="outlined"
          size="small"
          onClick={shiftToday}
          startIcon={<Today />}
        >
          Today
        </Button>

        <Tooltip title="Next week">
          <IconButton onClick={shiftNext} size="small">
            <ChevronRight />
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            px: 2,
            py: 0.5,
            bgcolor: 'action.hover',
            borderRadius: 1,
            fontWeight: 500,
          }}
        >
          {formattedDateRange}
        </Box>
      </Stack>

      {/* Right: Controls */}
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="timezone-label">Timezone</InputLabel>
          <Select
            labelId="timezone-label"
            value={timeZone}
            label="Timezone"
            onChange={(e) => onTimeZoneChange(e.target.value)}
          >
            {timeZoneOptions.map((tz) => (
              <MenuItem key={String(tz.value)} value={String(tz.value)}>
                {tz.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title="Refresh">
          <IconButton onClick={onRefresh} size="small">
            <Refresh />
          </IconButton>
        </Tooltip>

        <Tooltip title={isFullScreen ? 'Exit fullscreen' : 'Fullscreen'}>
          <IconButton onClick={onFullScreen} size="small">
            {isFullScreen ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}
